import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, inject, input, OnInit, Signal, signal } from '@angular/core';
import { MatChipsModule } from '@angular/material/chips';
import { CommonModule } from '@angular/common';
import { EditImageGalleryComponent } from "../edit-image-gallery/edit-image-gallery.component";
import { WebsiteStateService } from '../../../../state-management/services/website-state.service';
import { ImageGalleryImage, WebElement } from '../../../../state-management/model/interfaces/state.model';
import { DEFAULT_IMAGE_GALLERY_FILENAME } from '../../../../state-management/constants/global.constants';
import { ComponentActions } from '../../../../state-management/model/enums/state.enums';
import { CoordinatesSystem, ScreenWidthBreakpoints } from '../../../../state-management/model/enums/screen-size.enums';
import { ScreensizeStateService } from '../../../../state-management/services/screensize-state.service';


export interface SpecifiedImageGalleryImage extends ImageGalleryImage {
  showInGallery: boolean;
  showAsThumbnail: boolean;
  isPlaceholder: boolean;
  newTag?: string;
}

export interface ImageGalleryConfig {
  images: string[];
  thumbnails: string[];
}

@Component({
  selector: 'app-base-image-gallery',
  templateUrl: './base-image-gallery.component.html',
  styleUrl: './base-image-gallery.component.scss',
  imports: [
    MatChipsModule,
    CommonModule,
    EditImageGalleryComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class BaseImageGalleryComponent implements OnInit {
  readonly elemIds = input.required<string[]>();

  private stateService = inject(WebsiteStateService);
  private elements: Signal<Record<string, WebElement>> = this.stateService.elements;
  private actions = this.stateService.actions;

  public configId!: string;
  public allImages!: Signal<SpecifiedImageGalleryImage[]>;
  public selectedImage = signal<string | null>(null);

  public imageGalleryFolder = this.stateService.getImageGalleryStoragePath();
  public imageGalleryFileName = DEFAULT_IMAGE_GALLERY_FILENAME;

  public isSmallScreen$: Signal<boolean> = this.screensizeState.isScreenSizes(CoordinatesSystem.ScreenWidth, [ScreenWidthBreakpoints.XSmall, ScreenWidthBreakpoints.Small]);


  public activeTags = signal<string[]>([]);

  public openEidtGallery = false

  constructor(private screensizeState: ScreensizeStateService) {
    effect(() => {
      if (this.actions().includes(ComponentActions.EditGallery)) {
        this.stateService.removeAction(ComponentActions.EditGallery);
        this.openEidtGallery = true;
      }
    });
  }

  ngOnInit() {
    this.configId = this.getGalleryConfigId()
    this.allImages = this.stateService.getImageGalleryById(this.configId);
  }

  public thumbnailImages = computed(() => {
    const images = this.allImages()?.filter((img: SpecifiedImageGalleryImage) => img.showAsThumbnail);
    return images || [];
  });
  

  private getGalleryConfigId(): string {
    const galleryElement = this.elemIds()
      .map(id => this.elements()[id])
      .find(element => element && element.label === 'gallery');
    return galleryElement ? galleryElement.id : '';
  }

  uniqueTags(): string[] {
    const images = this.allImages().filter((img) => img.showInGallery);
    return [...new Set(images.flatMap((img) => img.tags))];
  }

  filteredImages() {    
    const uuid = this.selectedImage();
    let images = this.allImages().filter((img) => img.showInGallery);

    if (uuid) {
      const index = images.findIndex((img) => img.uuid === uuid);
      if (index > -1) {
      const [selectedImage] = images.splice(index, 1);
      images = [selectedImage, ...images];
      }
    }

    if (this.activeTags().includes("all")) {
      return images;
    }

    return this.activeTags().length ? images.filter((img) => img.tags.some((tag) => this.activeTags().includes(tag))) : images;
  }

  /** Aktualisiert die ausgewählten Filter */
  updateFilter(event: any) {
    this.activeTags.set(event.value);
  }

  onSlideChange($event: any	) {
    // const index  = $event.detail[0].activeIndex;
    // const selectedImage = this.filteredImages()[index];
    // this.selectedImage.set(selectedImage.uuid);
  }

  /** Öffnet die Lightbox */
  openImage(uuid: string) {
    this.selectedImage.set(uuid);
    this.activeTags.set(this.allImages().find((img) => img.uuid === uuid)?.tags || ["all"]);
  }

  /** Schließt die Lightbox */
  closeImageGallery() {
    this.selectedImage.set(null);
    this.openEidtGallery = false;
    this.activeTags.set([]);
  }

  closeImage(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.classList.contains('lightbox')) {
      this.openEidtGallery = false;
      this.selectedImage.set(null);
      this.activeTags.set([]);
    }
  }
}